import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Chip,
    Card,
    CardContent,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    List,
    ListItem,
    ListItemText,
    LinearProgress,
    MenuItem,
    Select,
    Step,
    StepLabel,
    Stepper,
    Switch,
    Tooltip,
    Typography,
    Autocomplete,
    TextField,
    Backdrop,
    CircularProgress,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ListIcon from '@mui/icons-material/List';
import PersonIcon from '@mui/icons-material/Person';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import RefreshIcon from '@mui/icons-material/Refresh';
import ReplayIcon from '@mui/icons-material/Replay';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { alpha } from '@mui/material/styles';

import { SCUIButton, SCUILoadingLinear } from '../../../atomic/atoms';
import { SCUIBreadCrumb } from '../../../atomic/molecules/SCUIBreadCrumb/SCUIBreadCrumb';
import { SCUIContent } from '../../../atomic/organisms/SCUIContent/SCUIContent';
import { SCUIS3Browser } from '../../../atomic/organisms/SCUIS3Browser/SCUIS3Browser';
import Helper from '../../../utils/helper';
import { generateUUID, handleStudioApiResponse } from '../../helper/studio_helper';
import { MannequinQuery, PlanningQuery, StudioFileManagerQuery } from '../../../services/studio/StudioQuery';
import { MasterDataQuery, OperatorQuery } from '../../../services/query';
import { CreateS3Folder } from '../../../services/command/aiPrompt/S3BrowserCommand.services';
import { FileTransferServiceClient } from './proto/FileTransferServiceClient';
import { StudioFileUploadDropzone } from '../../components/StudioFileUploadDropzone';
import { getUsername } from '../../../core/wrappers/axios-wrapper';
import { GetShootingTypeResponse, UpdateFileTransferRequestResponse } from '../../../types/StudioFileManager';
import { UploadItem, UploadStatus } from './grpcFileManagerTypes';
import {
    applyDevFailureSimulationFirstRound,
    buildBatchUploadStatusMessage,
    delayMs,
    filterPendingUploadItems,
    mergeRetryItemsIntoUploadList,
    prepareFailedItemsForRetry,
    registerUploadItemWithDuplicateCheck,
    runParallelUploadQueue,
    uploadSingleFileWithRetries,
} from './grpcFileManagerUploadHelpers';
import { handleGrpcUploadComplete, sendUpdateForAllRequestIds } from './grpcFileManagerCompleteHelpers';
import { executeSendUpdateFileTransferRequest } from './grpcFileManagerApiHelpers';
import { GrpcAddAuthorityControl, GrpcAuthorityRow } from './GrpcAuthorityRow';

type SelectionState = {
    shootingType: number | '';
    mannequinId: number | '';
};

type AuthorityItem = {
    id: string;
    titleId: number | '';
    employeeId: number | '';
};

/** Dosya seçimi sonrası: tarayıcı okuma + liste oluşturma (alt progress ile). */
type FileListIngestState =
    | null
    | { mode: 'readingNative' }
    | {
          mode: 'ingesting';
          current: number;
          total: number;
          currentSize?: number;
          totalSize?: number;
          currentFileName?: string;
      };

// Steps
const steps = [
    'studio.grpcFileManager.selection.title',
    'studio.upload.selectFiles',
    'aiPrompt.s3Browser.title',
    'general.summary',
];

/** Boyamadan önce ana iş parçacığını serbest bırakır (büyük dosya seçiminde boş ekranı önler). */
function yieldToPaint(): Promise<void> {
    return new Promise((resolve) => {
        requestAnimationFrame(() => {
            requestAnimationFrame(() => resolve());
        });
    });
}

const trimTrailingSlashes = (value: string): string => {
    let end = value.length;
    while (end > 0 && value[end - 1] === '/') {
        end -= 1;
    }
    return end === value.length ? value : value.slice(0, end);
};

/**
 * uniqueNumber sonrası / uzantı öncesi boşlukları temizler.
 * Örn. `8685004612378_2007   .jpg` → `8685004612378_2007.jpg`
 */
function normalizeUploadFileName(fileName: string): string {
    const trimmed = (fileName || '').trim().replace(/\\/g, '/');
    const name = trimmed.includes('/') ? trimmed.slice(trimmed.lastIndexOf('/') + 1) : trimmed;
    const lastDot = name.lastIndexOf('.');
    if (lastDot <= 0) {
        // sonar-disable-next-line S5852
        return name.replace(/\s+$/u, '');
    }
    // sonar-disable-next-line S5852
    const base = name.slice(0, lastDot).replace(/\s+$/u, '');
    const ext = name.slice(lastDot);
    return `${base}${ext}`;
}

/** Listede aynı anda çizilen satır üst sınırı (geri kalanı “tümünü göster” ile). */
const FILE_LIST_PREVIEW_LIMIT = 100;

function buildStudioS3ObjectKey(s3DestinationPath: string | undefined, fileName: string): string {
    if (!s3DestinationPath) return fileName;
    const clean = s3DestinationPath.endsWith('/') ? s3DestinationPath.slice(0, -1) : s3DestinationPath;
    return `${clean}/${fileName}`;
}

/** Upload / multipart için S3 dosya adı: final storedFilename öncelikli. */
function resolveUploadFileName(item: Pick<UploadItem, 'fileName' | 'storedFilename'>): string {
    const stored = item.storedFilename?.trim();
    return stored || item.fileName;
}

function createUploadItemFromFile(file: File): UploadItem {
    const rawFileName = file.name;
    const normalizedName = normalizeUploadFileName(rawFileName);
    return {
        clientId: generateUUID(),
        file,
        fileName: normalizedName,
        rawFileName,
        originalFilePath: normalizedName,
        totalSize: file.size,
        bytesSent: 0,
        progress: 0,
        status: UploadStatus.Pending,
        errorMessage: '',
        abortController: new AbortController(),
        currentAttempt: 0,
        maxAttempts: 3,
        fileTransferRequestId: null,
        fileTransferRequestDetailId: null,
    };
}

function formatTransferDuration(ms: number): string {
    if (!Number.isFinite(ms) || ms < 0) return '0:00';
    const totalSec = Math.floor(ms / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) {
        return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    }
    return `${m}:${String(s).padStart(2, '0')}`;
}

function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function getUploadStatusText(uploadStatus: UploadStatus, t: (key: string) => string): string {
    switch (uploadStatus) {
        case UploadStatus.Pending:
            return t('studio.upload.ready');
        case UploadStatus.Uploading:
            return t('studio.upload.uploading');
        case UploadStatus.Completed:
            return t('studio.upload.completed');
        case UploadStatus.Failed:
            return t('studio.upload.failed');
        case UploadStatus.Cancelled:
            return t('studio.upload.cancelled');
        default:
            return '';
    }
}

function getUploadChipColor(uploadStatus: UploadStatus): 'success' | 'error' | 'warning' | 'primary' {
    switch (uploadStatus) {
        case UploadStatus.Completed:
            return 'success';
        case UploadStatus.Failed:
            return 'error';
        case UploadStatus.Cancelled:
            return 'warning';
        default:
            return 'primary';
    }
}

export const GRPCFileManager: React.FC = () => {
    const [activeStep, setActiveStep] = useState<number>(0);
    const [status, setStatus] = useState<string>('');
    const [uploadItems, setUploadItemsState] = useState<UploadItem[]>([]);
    const uploadItemsRef = useRef<UploadItem[]>([]);

    useEffect(() => {
        uploadItemsRef.current = uploadItems;
    }, [uploadItems]);

    const setUploadItems = useCallback((action: React.SetStateAction<UploadItem[]>) => {
        setUploadItemsState((prev) => {
            const next = typeof action === 'function' ? action(prev) : action;
            uploadItemsRef.current = next;
            return next;
        });
    }, []);

    const readCurrentUploadItems = (): UploadItem[] => uploadItemsRef.current;
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isSelectionLoading, setIsSelectionLoading] = useState<boolean>(false);
    const [employees, setEmployees] = useState<any[]>([]);
    const [employeeTitles, setEmployeeTitles] = useState<any[]>([]);
    const [mannequins, setMannequins] = useState<any[]>([]);
    const [shootingTypes, setShootingTypes] = useState<GetShootingTypeResponse[]>([]);
    const [authorities, setAuthorities] = useState<AuthorityItem[]>([]);
    const [countryList, setCountryList] = useState<any[]>([]);
    const [selectedCountry, setSelectedCountry] = useState<any>(null);
    const [canSelectCountry, setCanSelectCountry] = useState<boolean>(false);
    const [fileTransferRequestId, setFileTransferRequestId] = useState<number | null>(null);
    const [, setUpdateResult] = useState<UpdateFileTransferRequestResponse | null>(null);
    const [selection, setSelection] = useState<SelectionState>({
        shootingType: '',
        mannequinId: '',
    });
    const fileInputRef = useRef<HTMLInputElement>(null);
    /** Kullanıcı sistem dosya penceresini açtı; focus dönüşünde alt bar gösterilir (Open ile change arası). */
    const waitingNativePickerReturnRef = useRef(false);
    const fileListIngestPhaseRef = useRef<FileListIngestState>(null);
    const fileIngestGenerationRef = useRef(0);

    const MAX_PARALLEL_UPLOADS = 8;
    const grpcClientRef = useRef(new FileTransferServiceClient());
    /** Dev: localStorage STUDIO_SIMULATE_UPLOAD_FAILURES = "2" → ilk 2 dosya yüklemede başarısız sayılır (test için) */
    const devSimulatedFailureFileNamesRef = useRef<Set<string>>(new Set());
    const batchUploadCountsRef = useRef({ ok: 0, fail: 0 });
    const uploadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const uploadStartedAtRef = useRef<number | null>(null);

    const [transferElapsedMs, setTransferElapsedMs] = useState(0);
    const [lastTransferDurationMs, setLastTransferDurationMs] = useState<number | null>(null);
    const [uploadStatusAccordionExpanded, setUploadStatusAccordionExpanded] = useState(false);
    const [ongoingSubExpanded, setOngoingSubExpanded] = useState<boolean>(true);
    const [successSubExpanded, setSuccessSubExpanded] = useState<boolean>(false);
    const [failedSubExpanded, setFailedSubExpanded] = useState<boolean>(true);

    const ongoingGroup = useMemo(() => {
        return uploadItems.filter(
            (item) => item.status === UploadStatus.Uploading || item.status === UploadStatus.Pending
        );
    }, [uploadItems]);

    const successGroup = useMemo(() => {
        return uploadItems.filter((item) => item.status === UploadStatus.Completed);
    }, [uploadItems]);

    const failedGroup = useMemo(() => {
        return uploadItems.filter(
            (item) => item.status === UploadStatus.Failed || item.status === UploadStatus.Cancelled
        );
    }, [uploadItems]);
    const [fileListIngest, setFileListIngest] = useState<FileListIngestState>(null);
    const [earlyBottomAfterNativePicker, setEarlyBottomAfterNativePicker] = useState(false);
    const [fileListPreviewExpanded, setFileListPreviewExpanded] = useState(false);
    const [s3DestinationPath, setS3DestinationPath] = useState<string>('');
    const [autoRetryFailed, setAutoRetryFailed] = useState(false);
    const [isAutoRetryInProgress, setIsAutoRetryInProgress] = useState(false);
    const autoRetryFailedRef = useRef(false);
    const uploadSessionLockRef = useRef(false);
    const autoRetryEffectActiveRef = useRef(false);

    const { t } = useTranslation();

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isUploading) {
                e.preventDefault();
                // eslint-disable-next-line @typescript-eslint/no-deprecated
                e.returnValue = '';
                return '';
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isUploading]);

    const studioIglooShareS3Prefix = useMemo(() => {
        const envPrefix = trimTrailingSlashes(String(import.meta.env.VITE_ENVIRONMENT ?? '').trim());
        const base = envPrefix ? `${envPrefix}/igloo_share/` : 'igloo_share/';

        const now = new Date();
        const year = now.getFullYear();
        const monthNum = String(now.getMonth() + 1).padStart(2, '0');

        const turkishMonths = [
            'OCAK',
            'ŞUBAT',
            'MART',
            'NİSAN',
            'MAYIS',
            'HAZİRAN',
            'TEMMUZ',
            'AĞUSTOS',
            'EYLÜL',
            'EKİM',
            'KASIM',
            'ARALIK',
        ];
        const monthName = turkishMonths[now.getMonth()];
        const monthFolder = `${monthNum}_${monthName}`;

        const day = String(now.getDate()).padStart(2, '0');
        const dateStr = `${day}.${monthNum}.${year}`;
        const yearFolder = `${year}ÇEKİMLER`;

        let username = 'SYSTEM';
        try {
            const checkOperator = JSON.parse(localStorage.getItem('checkOperator') || '{}');
            if (checkOperator?.username) {
                username = String(checkOperator.username).toLocaleUpperCase('tr-TR');
            }
        } catch (e) {
            console.error('Error getting username for S3 prefix', e);
        }

        return `${base}${yearFolder}/${monthFolder}/${dateStr}/${username}/`;
    }, []);

    const isS3DestinationUnderIglooShare = useMemo(() => {
        const base = trimTrailingSlashes(studioIglooShareS3Prefix);
        const dest = trimTrailingSlashes(s3DestinationPath || '');
        if (!dest) return false;
        return dest === base || dest.startsWith(`${base}/`);
    }, [s3DestinationPath, studioIglooShareS3Prefix]);

    const isBuildingFileList = fileListIngest !== null;
    const isFileListPrepareUi = isBuildingFileList || earlyBottomAfterNativePicker;

    useEffect(() => {
        fileListIngestPhaseRef.current = fileListIngest;
    }, [fileListIngest]);

    useEffect(() => {
        if (activeStep !== 1) {
            waitingNativePickerReturnRef.current = false;
            setEarlyBottomAfterNativePicker(false);
        }
    }, [activeStep]);

    useEffect(() => {
        if (activeStep !== 1) {
            return;
        }
        const el = fileInputRef.current;
        if (!el) {
            return;
        }
        const onNativeFilePickerCanceled = () => {
            waitingNativePickerReturnRef.current = false;
            setEarlyBottomAfterNativePicker(false);
        };
        el.addEventListener('cancel', onNativeFilePickerCanceled);
        return () => el.removeEventListener('cancel', onNativeFilePickerCanceled);
    }, [activeStep]);

    useEffect(() => {
        if (activeStep !== 1) {
            return;
        }
        let fallbackTimer: ReturnType<typeof globalThis.setTimeout> | undefined;

        const onWindowFocus = () => {
            if (!waitingNativePickerReturnRef.current) {
                return;
            }
            flushSync(() => setEarlyBottomAfterNativePicker(true));
            if (fallbackTimer !== undefined) {
                globalThis.clearTimeout(fallbackTimer);
            }
            fallbackTimer = globalThis.setTimeout(() => {
                if (!waitingNativePickerReturnRef.current) {
                    return;
                }
                const inp = fileInputRef.current;
                const hasFiles = !!inp?.files?.length;
                const ingestActive = fileListIngestPhaseRef.current !== null;
                if (!hasFiles && !ingestActive) {
                    waitingNativePickerReturnRef.current = false;
                    setEarlyBottomAfterNativePicker(false);
                }
            }, 900);
        };

        globalThis.addEventListener('focus', onWindowFocus);
        return () => {
            globalThis.removeEventListener('focus', onWindowFocus);
            if (fallbackTimer !== undefined) {
                globalThis.clearTimeout(fallbackTimer);
            }
        };
    }, [activeStep]);

    const completedFilesCount = useMemo(
        () => uploadItems.filter((i) => i.status === UploadStatus.Completed).length,
        [uploadItems]
    );
    const showCompleteButton = useMemo(() => {
        if (uploadItems.length === 0 || isUploading) return true;
        const allTerminal = uploadItems.every(
            (i) =>
                i.status === UploadStatus.Completed ||
                i.status === UploadStatus.Failed ||
                i.status === UploadStatus.Cancelled
        );
        const uploadStarted = uploadItems.some((i) => i.status !== UploadStatus.Pending);
        return !(allTerminal && uploadStarted);
    }, [uploadItems, isUploading]);

    useEffect(() => {
        autoRetryFailedRef.current = autoRetryFailed;
    }, [autoRetryFailed]);

    useEffect(() => {
        return () => {
            if (uploadTimerRef.current != null) {
                clearInterval(uploadTimerRef.current);
                uploadTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        if (activeStep === 2) {
            setUploadStatusAccordionExpanded(false);
        }
    }, [activeStep]);

    useEffect(() => {
        if (activeStep === 2 && studioIglooShareS3Prefix) {
            const cleanPrefix = studioIglooShareS3Prefix.replace(/\/$/, '');
            const lastSlashIndex = cleanPrefix.lastIndexOf('/');
            if (lastSlashIndex >= 0) {
                const parentPrefix = cleanPrefix.substring(0, lastSlashIndex + 1);
                const folderName = cleanPrefix.substring(lastSlashIndex + 1);

                const preCreateS3Folder = async () => {
                    try {
                        await CreateS3Folder({
                            prefix: parentPrefix,
                            name: folderName,
                        });
                        console.log('Pre-created S3 folder:', cleanPrefix);
                    } catch (e) {
                        console.error('Failed to pre-create S3 folder', e);
                    }
                };
                preCreateS3Folder();
            }
        }
    }, [activeStep, studioIglooShareS3Prefix]);

    const shootingTypeOptions = useMemo(
        () =>
            shootingTypes.map((type) => ({
                value: type.shootingTypeId,
                label: type.shootingTypeName,
                key: type.shootingTypeKey,
                isMannequinMandatory: type.isMannequinMandatory,
            })),
        [shootingTypes]
    );

    const selectedShootingTypeObj = useMemo(
        () => shootingTypeOptions.find((o) => o.value === selection.shootingType),
        [shootingTypeOptions, selection.shootingType]
    );

    const isMannequinMandatory = !!selectedShootingTypeObj?.isMannequinMandatory;
    const canManageAuthorities = !!selection.shootingType && (!isMannequinMandatory || !!selection.mannequinId);

    const hasAuthorityRows = authorities.length > 0;
    const authoritiesValid = authorities.every((item) => item.titleId && item.employeeId);
    const authoritiesSelectionValid = hasAuthorityRows && authoritiesValid;

    // Validation: Mannequin is only required if shooting type mandates it
    const mannequinSelectionValid = !isMannequinMandatory || !!selection.mannequinId;

    const selectionIsValid =
        selectedCountry && selection.shootingType && mannequinSelectionValid && authoritiesSelectionValid;

    const isStep1Valid = selectionIsValid;
    const isStep2Valid = uploadItems.length > 0 && !isFileListPrepareUi;

    const visibleUploadItems = useMemo(() => {
        if (fileListPreviewExpanded || uploadItems.length <= FILE_LIST_PREVIEW_LIMIT) {
            return uploadItems;
        }
        return uploadItems.slice(0, FILE_LIST_PREVIEW_LIMIT);
    }, [uploadItems, fileListPreviewExpanded]);

    const isFileListPreviewTruncated = uploadItems.length > FILE_LIST_PREVIEW_LIMIT && !fileListPreviewExpanded;

    const ingestFilesFromFileList = async (files: File[]) => {
        const gen = ++fileIngestGenerationRef.current;
        if (files.length === 0) {
            return;
        }

        const totalSize = files.reduce((acc, f) => acc + f.size, 0);
        let currentSizeProcessed = 0;

        flushSync(() => {
            setFileListIngest({
                mode: 'ingesting',
                current: 0,
                total: files.length,
                currentSize: 0,
                totalSize,
                currentFileName: '',
            });
            setUploadItems([]);
        });
        await yieldToPaint();

        const items: UploadItem[] = [];
        /** Normalize key → items içindeki kazanan index. Temiz ad (raw===normalized) tercih edilir. */
        const winnerIndexByNormalized = new Map<string, number>();
        const n = files.length;
        const duplicateMessage = t('studio.upload.duplicateNormalizedFileName');

        for (let i = 0; i < n; i++) {
            if (fileIngestGenerationRef.current !== gen) {
                return;
            }
            const file = files[i];
            const item = createUploadItemFromFile(file);
            registerUploadItemWithDuplicateCheck(items, winnerIndexByNormalized, item, duplicateMessage);
            currentSizeProcessed += file.size;
            const done = i + 1;
            setFileListIngest({
                mode: 'ingesting',
                current: done,
                total: n,
                currentSize: currentSizeProcessed,
                totalSize,
                currentFileName: file.name,
            });
            await new Promise<void>((resolve) => {
                requestAnimationFrame(() => resolve());
            });
        }

        if (fileIngestGenerationRef.current !== gen) {
            return;
        }
        setUploadItems(items);
        setFileListPreviewExpanded(false);
        setFileListIngest(null);
    };

    const handleNext = () => {
        setActiveStep((prev) => prev + 1);
    };

    const handleBack = () => {
        setActiveStep((prev) => prev - 1);
    };

    const getEmployeesByTitleId = (titleId: number | '') => {
        if (!titleId) {
            return employees;
        }
        return employees.filter((employee: any) => employee.title === titleId);
    };

    const addAuthority = () => {
        setAuthorities((prev) => [...prev, { id: generateUUID(), titleId: '', employeeId: '' }]);
    };

    const removeAuthority = (id: string) => {
        setAuthorities((prev) => prev.filter((item) => item.id !== id));
    };

    const updateAuthority = (id: string, changes: Partial<AuthorityItem>) => {
        setAuthorities((prev) => prev.map((item) => (item.id === id ? { ...item, ...changes } : item)));
    };

    const handleShootingTypeChange = (shootingType: number) => {
        setAuthorities([]);
        setSelection((prev) => ({
            ...prev,
            shootingType,
            mannequinId: '',
        }));
    };

    const handleMannequinChange = (mannequinId: number) => {
        setAuthorities([]);
        setSelection((prev) => ({
            ...prev,
            mannequinId,
        }));
    };

    useEffect(() => {
        let pendingRequests = 3;
        const handleFinally = () => {
            pendingRequests -= 1;
            if (pendingRequests <= 0) {
                setIsSelectionLoading(false);
            }
        };

        setIsSelectionLoading(true);

        handleStudioApiResponse(
            PlanningQuery.GetPlanningPageData(),
            (res: any) => {
                setEmployees(res.data?.employees || []);
                setEmployeeTitles(res.data?.employeeTitles || []);
            },
            handleFinally,
            t
        );

        handleStudioApiResponse(
            MannequinQuery.GetMannequins({}),
            (res: any) => {
                setMannequins(res.data?.items || res.data || []);
            },
            handleFinally,
            t
        );

        handleStudioApiResponse(
            StudioFileManagerQuery.GetShootingTypes(),
            (res: any) => {
                setShootingTypes(res.data || res || []);
            },
            handleFinally,
            t
        );

        MasterDataQuery.GetCountryList().then((res: any) => {
            let parseData = res.data.map((item: any) => {
                return {
                    id: item.countryId,
                    key: item.countryKey,
                    name: item.countryCode,
                    dropdownValueText: item.translatedCountryName,
                };
            });
            setCountryList(parseData);

            const defaultCountry = parseData.find((c: any) => c.name === 'TR');
            setSelectedCountry(defaultCountry || null);
        });

        OperatorQuery.GetOperatorRolesList(Helper.getUserRef()).then((res: any) => {
            console.log(res);
            const roles = res.model?.data || [];
            console.log('User Roles (from API):', roles);

            const hasCountrySelectRole = roles.some(
                (role: any) => role.roleNameKey === 'bo_StudioFileManagerCountrySelecter'
            );
            setCanSelectCountry(hasCountrySelectRole);
        });
    }, [t]);

    const handleFilesSelected = async (selectedFiles: File[]) => {
        waitingNativePickerReturnRef.current = false;
        flushSync(() => {
            setEarlyBottomAfterNativePicker(false);
            setFileListIngest({ mode: 'readingNative' });
        });
        await yieldToPaint();

        if (selectedFiles.length === 0) {
            flushSync(() => setFileListIngest(null));
            return;
        }
        await ingestFilesFromFileList(selectedFiles);
    };

    const handleNativeFilePickerOpen = () => {
        waitingNativePickerReturnRef.current = true;
    };

    const handleNativeFilePickerCancel = () => {
        waitingNativePickerReturnRef.current = false;
        setEarlyBottomAfterNativePicker(false);
    };

    const removeFile = (clientId: string) => {
        setUploadItems((prev) => prev.filter((item) => item.clientId !== clientId));
    };

    const clearAllFiles = () => {
        fileIngestGenerationRef.current += 1;
        waitingNativePickerReturnRef.current = false;
        setEarlyBottomAfterNativePicker(false);
        setFileListIngest(null);
        setUploadItems([]);
        setFileListPreviewExpanded(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const prepareFailedItemsForRetryLocal = prepareFailedItemsForRetry;

    const stopUploadTimer = () => {
        if (uploadTimerRef.current != null) {
            clearInterval(uploadTimerRef.current);
            uploadTimerRef.current = null;
        }
        if (uploadStartedAtRef.current != null) {
            const duration = Math.round(performance.now() - uploadStartedAtRef.current);
            setLastTransferDurationMs(duration);
            uploadStartedAtRef.current = null;
        }
    };

    const startUploadTimer = () => {
        if (uploadTimerRef.current != null) {
            clearInterval(uploadTimerRef.current);
            uploadTimerRef.current = null;
        }
        uploadStartedAtRef.current = performance.now();
        setTransferElapsedMs(0);
        setLastTransferDurationMs(null);
        uploadTimerRef.current = setInterval(() => {
            if (uploadStartedAtRef.current != null) {
                setTransferElapsedMs(Math.round(performance.now() - uploadStartedAtRef.current));
            }
        }, 250);
    };

    const runAutoRetryUploadRound = async (pendingItems: UploadItem[]): Promise<UploadItem[] | null> => {
        await processUploadQueue(pendingItems);

        if (!autoRetryFailedRef.current) {
            return null;
        }

        await delayMs(1000);
        if (!autoRetryFailedRef.current) {
            return null;
        }

        const failedItems = readCurrentUploadItems().filter((item) => item.status === UploadStatus.Failed);
        if (failedItems.length === 0) {
            return null;
        }

        const retriedItems = prepareFailedItemsForRetryLocal(failedItems);
        setIsAutoRetryInProgress(true);
        setUploadItems((prev) => mergeRetryItemsIntoUploadList(prev, retriedItems));
        setStatus(
            t('studio.upload.autoRetryingFailed', {
                count: retriedItems.length,
            })
        );
        return retriedItems;
    };

    const startUpload = async (sourceOverride?: UploadItem[]): Promise<boolean> => {
        if (uploadSessionLockRef.current) {
            return false;
        }

        const source = sourceOverride ?? uploadItems;
        if (source.length === 0) {
            setStatus(t('studio.upload.selectFiles'));
            return false;
        }

        setIsUploading(true);
        setStatus(t('studio.upload.uploadStarted'));

        let pendingItems = filterPendingUploadItems(source);

        if (pendingItems.length === 0) {
            setStatus(t('studio.upload.noFilesToUpload'));
            return false;
        }

        uploadSessionLockRef.current = true;
        setIsUploading(true);
        const startingAutoRetry =
            autoRetryFailedRef.current && pendingItems.some((item) => item.status === UploadStatus.Failed);
        setIsAutoRetryInProgress(startingAutoRetry);
        setStatus(startingAutoRetry ? t('studio.upload.autoRetryInProgress') : t('studio.upload.uploadStarted'));

        startUploadTimer();

        try {
            let isFirstUploadRound = true;
            while (pendingItems.length > 0) {
                if (isFirstUploadRound) {
                    applyDevFailureSimulationFirstRound(pendingItems, devSimulatedFailureFileNamesRef.current);
                    isFirstUploadRound = false;
                } else {
                    devSimulatedFailureFileNamesRef.current = new Set();
                }

                const nextPendingItems = await runAutoRetryUploadRound(pendingItems);
                if (!nextPendingItems) {
                    break;
                }
                pendingItems = nextPendingItems;
            }

            return true;
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : String(error);
            setStatus(`${t('studio.upload.uploadError')}: ${message}`);
            return false;
        } finally {
            stopUploadTimer();
            setIsAutoRetryInProgress(false);
            setIsUploading(false);
            uploadSessionLockRef.current = false;
        }
    };

    const uploadSingleFile = async (item: UploadItem): Promise<void> => {
        await uploadSingleFileWithRetries({
            item,
            s3DestinationPath,
            grpcClient: grpcClientRef.current,
            getUsername,
            t,
            resolveUploadFileName,
            buildS3ObjectKey: buildStudioS3ObjectKey,
            updateUploadItems: setUploadItems,
            devSimulatedFailureFileNames: devSimulatedFailureFileNamesRef.current,
            batchUploadCounts: batchUploadCountsRef.current,
            missingDetailMessage: t('studio.upload.createMatchMissing'),
        });
    };

    const processUploadQueue = async (allItems: UploadItem[]) => {
        batchUploadCountsRef.current = { ok: 0, fail: 0 };
        await runParallelUploadQueue({
            allItems,
            maxParallel: MAX_PARALLEL_UPLOADS,
            uploadOne: uploadSingleFile,
        });
        const { ok, fail } = batchUploadCountsRef.current;
        setStatus(buildBatchUploadStatusMessage(ok, fail, t));
    };

    // const cancelUpload = (item: UploadItem) => {
    //     item.abortController?.abort();
    //     setUploadItems((prev) =>
    //         prev.map((ui) => (ui.clientId === item.clientId ? { ...ui, status: UploadStatus.Cancelled } : ui))
    //     );
    // };

    // const retryUpload = async (item: UploadItem) => {
    //     const updatedItem: UploadItem = {
    //         ...item,
    //         status: UploadStatus.Pending,
    //         abortController: new AbortController(),
    //         errorMessage: '',
    //         bytesSent: 0,
    //         progress: 0,
    //         currentAttempt: 0,
    //     };

    //     setUploadItems((prev) => prev.map((ui) => (ui.clientId === item.clientId ? updatedItem : ui)));

    //     await uploadSingleFile(updatedItem);
    // };

    const renderUploadItemCard = (item: UploadItem) => (
        <Box
            key={item.clientId}
            sx={{
                p: 1.5,
                border: '1px solid #e2e8f0',
                borderRadius: 1.5,
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                mb: 1.5,
                '&:last-child': { mb: 0 },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                <Typography variant="h6" sx={{ fontSize: '13px', fontWeight: 600, flex: 1, wordBreak: 'break-all' }}>
                    {item.rawFileName || item.fileName}
                </Typography>
                <Tooltip title={item.errorMessage || ''} disableHoverListener={!item.errorMessage}>
                    <Chip
                        label={`${getUploadStatusText(item.status, t)} (${item.progress}%)`}
                        size="small"
                        color={getUploadChipColor(item.status)}
                        sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '10px' } }}
                    />
                </Tooltip>
            </Box>
            <LinearProgress variant="determinate" value={item.progress} sx={{ height: 6, borderRadius: 3 }} />
            {!!item.errorMessage && (item.status === UploadStatus.Failed || item.status === UploadStatus.Uploading) && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        mt: 0.75,
                        color: item.status === UploadStatus.Failed ? '#c62828' : '#e65100',
                        wordBreak: 'break-word',
                        fontSize: '11px',
                    }}
                >
                    {item.status === UploadStatus.Failed
                        ? t('studio.upload.failureReason', {
                              detail: item.errorMessage,
                          })
                        : item.errorMessage}
                </Typography>
            )}
        </Box>
    );

    const sendUpdateFileTransferRequest = (requestId: number): Promise<void> =>
        executeSendUpdateFileTransferRequest(requestId, readCurrentUploadItems(), {
            t,
            setUpdateResult,
            setStatus,
            setIsSelectionLoading,
        });

    const handleComplete = async () => {
        await handleGrpcUploadComplete({
            t,
            uploadItems,
            fileTransferRequestId,
            s3DestinationPath,
            selectedCountry,
            selection,
            authorities,
            setFileTransferRequestId,
            setUploadItems,
            setUpdateResult,
            setStatus,
            setIsSelectionLoading,
            setUploadStatusAccordionExpanded,
            setOngoingSubExpanded,
            setSuccessSubExpanded,
            setFailedSubExpanded,
            startUpload,
            sendUpdateFileTransferRequest,
        });
    };

    const getSelectionSummary = () => {
        const selectedShootingType = shootingTypeOptions.find((o) => o.value === selection.shootingType)?.label;
        const selectedMannequinObj = mannequins.find((m: any) => m.id === selection.mannequinId);

        return {
            country: selectedCountry?.dropdownValueText,
            shootingType: selectedShootingType,
            mannequin: selectedMannequinObj?.name,
            mannequinIsAi: selectedMannequinObj?.isAi ?? false,
            authoritiesCount: authorities.length,
            filesCount: uploadItems.length,
        };
    };

    useEffect(() => {
        if (!autoRetryFailed || isUploading || isSelectionLoading) {
            return;
        }
        if (uploadSessionLockRef.current || autoRetryEffectActiveRef.current) {
            return;
        }
        if (!fileTransferRequestId) {
            return;
        }

        // Sadece S3 aşamasında başarısız olmuş gerçek kayıtlar varsa otomatik yeniden denemeyi tetikle!
        const hasActualFailedUploads = uploadItems.some(
            (item) =>
                item.status === UploadStatus.Failed &&
                item.fileTransferRequestDetailId != null &&
                !!item.storedFilename?.trim()
        );
        if (!hasActualFailedUploads) {
            return;
        }

        autoRetryEffectActiveRef.current = true;

        const runAutoRetryUpdate = async () => {
            try {
                setUpdateResult(null);
                const uploadSuccess = await startUpload();
                if (!uploadSuccess) {
                    return;
                }
                await sendUpdateForAllRequestIds(uploadItems, sendUpdateFileTransferRequest);
            } finally {
                autoRetryEffectActiveRef.current = false;
            }
        };
        runAutoRetryUpdate();
        // Triggers when switch is enabled or a batch ends with failures while switch is on
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRetryFailed, isUploading, isSelectionLoading, fileTransferRequestId, uploadItems]);

    const resetForm = () => {
        setActiveStep(0);
        setStatus('');
        setUploadItems([]);
        setIsUploading(false);
        setAuthorities([]);
        setSelection({ shootingType: '', mannequinId: '' });
        setFileTransferRequestId(null);
        setUpdateResult(null);
        setTransferElapsedMs(0);
        setLastTransferDurationMs(null);
        setUploadStatusAccordionExpanded(false);
        setAutoRetryFailed(false);
        setIsAutoRetryInProgress(false);
        autoRetryFailedRef.current = false;
        fileIngestGenerationRef.current += 1;
        setFileListIngest(null);
        setFileListPreviewExpanded(false);
        setS3DestinationPath('');
        const defaultCountry = countryList.find((c: any) => c.name === 'TR');
        setSelectedCountry(defaultCountry || null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <SCUIContent>
            <Prompt when={isUploading} message={t('studio.upload.leaveConfirmMessage')} />
            <Backdrop
                sx={{
                    color: '#fff',
                    zIndex: (theme) => theme.zIndex.drawer + 999,
                    backdropFilter: 'blur(4px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.75)',
                }}
                open={isFileListPrepareUi}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '90%',
                        maxWidth: 600,
                        textAlign: 'center',
                        p: 4,
                        borderRadius: 3,
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
                        border: '1px solid rgba(255, 255, 255, 0.18)',
                    }}
                >
                    <CircularProgress color="primary" size={50} sx={{ mb: 3 }} />
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        {fileListIngest?.mode === 'ingesting'
                            ? `Dosya hazırlanıyor (${fileListIngest.current} / ${fileListIngest.total})`
                            : 'Dosyalar taranıyor...'}
                    </Typography>
                    {fileListIngest?.mode === 'ingesting' && fileListIngest.currentFileName && (
                        <Typography
                            variant="body2"
                            sx={{
                                mb: 3,
                                color: 'rgba(255, 255, 255, 0.7)',
                                wordBreak: 'break-all',
                                fontStyle: 'italic',
                                px: 2,
                            }}
                        >
                            {fileListIngest.currentFileName}
                        </Typography>
                    )}
                    <Box sx={{ width: '100%', mb: 2 }}>
                        <LinearProgress
                            variant={fileListIngest?.mode === 'ingesting' ? 'determinate' : 'indeterminate'}
                            value={
                                fileListIngest?.mode === 'ingesting' && fileListIngest.total > 0
                                    ? Math.min(100, (fileListIngest.current / fileListIngest.total) * 100)
                                    : undefined
                            }
                            sx={{
                                height: 10,
                                borderRadius: 5,
                                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 5,
                                },
                            }}
                        />
                    </Box>
                    {fileListIngest?.mode === 'ingesting' &&
                        fileListIngest.totalSize !== undefined &&
                        fileListIngest.currentSize !== undefined && (
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 500 }}>
                                İlerleme: {Math.round((fileListIngest.currentSize / fileListIngest.totalSize) * 100)}% (
                                {formatBytes(fileListIngest.currentSize)} / {formatBytes(fileListIngest.totalSize)})
                            </Typography>
                        )}
                </Box>
            </Backdrop>

            <SCUIBreadCrumb onlyTitle={t('studio.fileManager.title')} />
            <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 4 }}>
                    <Stepper
                        activeStep={activeStep}
                        sx={{
                            '& .MuiStepLabel-label': { fontSize: '1.1rem' },
                            '& .MuiStepIcon-root': { fontSize: '2rem' },
                            '& .MuiStepIcon-text': { fill: '#fff' },
                        }}
                    >
                        {steps.map((label) => (
                            <Step key={label}>
                                <StepLabel>{t(label)}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                </Box>

                {/* Step 1: Selection */}
                {activeStep === 0 && (
                    <Box
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 3,
                            border: '1px solid #e0e0e0',
                            backgroundColor: '#fff',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <PhotoCameraIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {t('studio.grpcFileManager.selection.title')}
                                </Typography>
                            </Box>
                            <IconButton
                                onClick={resetForm}
                                size="small"
                                title={t('studio.grpcFileManager.resetForm')}
                                sx={{
                                    color: 'primary.main',
                                    '&:hover': { backgroundColor: '#e3f2fd' },
                                }}
                            >
                                <RefreshIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                            {t('studio.grpcFileManager.selection.subtitle')}
                        </Typography>
                        <SCUILoadingLinear display={isSelectionLoading} />
                        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Autocomplete
                                options={countryList || []}
                                getOptionLabel={(option: any) => option?.dropdownValueText || ''}
                                value={selectedCountry}
                                disabled={!canSelectCountry}
                                onChange={(_, newValue) => {
                                    setSelectedCountry(newValue);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t('emailTemplateBuilder.selectCountryName')}
                                        size="small"
                                    />
                                )}
                                disablePortal
                                fullWidth
                            />
                            <FormControl fullWidth size="small">
                                <InputLabel>{t('studio.grpcFileManager.shootingType.label')}</InputLabel>
                                <Select
                                    value={selection.shootingType}
                                    label={t('studio.grpcFileManager.shootingType.label')}
                                    displayEmpty
                                    renderValue={(selected) => {
                                        if (selected === null) {
                                            return <span style={{ color: '#aaa' }}>{t('general.select')}</span>;
                                        }
                                        const selectedOption = shootingTypeOptions.find((o) => o.value === selected);
                                        return selectedOption ? selectedOption.label : '';
                                    }}
                                    onChange={(event) => {
                                        handleShootingTypeChange(event.target.value as number);
                                    }}
                                >
                                    <MenuItem disabled value="">
                                        <em>{t('general.select')}</em>
                                    </MenuItem>
                                    {shootingTypeOptions.map((option) => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {isMannequinMandatory && (
                                <FormControl fullWidth size="small">
                                    <InputLabel>{t('studio.grpcFileManager.mannequin')}</InputLabel>
                                    <Select
                                        value={selection.mannequinId}
                                        label={t('studio.grpcFileManager.mannequin')}
                                        renderValue={(selected) => {
                                            const mannequin = mannequins.find((m: any) => m.id === selected);
                                            if (!mannequin) return '';
                                            return (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    {mannequin.isAi ? (
                                                        <SmartToyIcon sx={{ fontSize: 20, color: '#7c4dff' }} />
                                                    ) : (
                                                        <PersonIcon sx={{ fontSize: 20, color: '#1976d2' }} />
                                                    )}
                                                    <span>{mannequin.name || mannequin.title}</span>
                                                </Box>
                                            );
                                        }}
                                        onChange={(event) => {
                                            handleMannequinChange(event.target.value as number);
                                        }}
                                    >
                                        <MenuItem value="">
                                            <em>{t('general.select')}</em>
                                        </MenuItem>
                                        {mannequins.map((mannequin: any) => (
                                            <MenuItem
                                                key={mannequin.id}
                                                value={mannequin.id}
                                                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                            >
                                                {mannequin.isAi ? (
                                                    <SmartToyIcon sx={{ fontSize: 20, color: '#7c4dff' }} />
                                                ) : (
                                                    <PersonIcon sx={{ fontSize: 20, color: '#1976d2' }} />
                                                )}
                                                {mannequin.name || mannequin.title}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                            {!!selection.shootingType && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {authorities.map((authority) => (
                                        <GrpcAuthorityRow
                                            key={authority.id}
                                            authority={authority}
                                            employeeTitles={employeeTitles}
                                            authorityEmployees={getEmployeesByTitleId(authority.titleId)}
                                            canManageAuthorities={canManageAuthorities}
                                            onUpdate={updateAuthority}
                                            onRemove={removeAuthority}
                                        />
                                    ))}
                                    <GrpcAddAuthorityControl
                                        canManageAuthorities={canManageAuthorities}
                                        onAdd={addAuthority}
                                    />
                                </Box>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                            <SCUIButton
                                type="primary"
                                sizeType="default"
                                size="s"
                                text={t('general.next')}
                                onClick={handleNext}
                                disabled={!isStep1Valid || isSelectionLoading}
                                showStartIcon
                                startIcon={<ArrowForwardIcon sx={{ color: '#fff' }} />}
                            />
                        </Box>
                    </Box>
                )}

                {/* Step 2: File Selection */}
                {activeStep === 1 && (
                    <Box
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 3,
                            border: '1px solid #e0e0e0',
                            backgroundColor: '#fff',
                            position: 'relative',
                            minHeight: { xs: 420, md: 520 },
                        }}
                    >
                        <StudioFileUploadDropzone
                            onFilesSelected={handleFilesSelected}
                            disabled={isBuildingFileList}
                            accept="*/*"
                            inputRef={fileInputRef}
                            onBrowseOpen={handleNativeFilePickerOpen}
                            onBrowseCancel={handleNativeFilePickerCancel}
                        />

                        {/* File List Preview */}
                        {uploadItems.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        mb: 1,
                                    }}
                                >
                                    <Typography variant="subtitle2">
                                        {t('studio.upload.selectedFiles')} ({uploadItems.length})
                                    </Typography>
                                    <SCUIButton
                                        type="secondary"
                                        sizeType="default"
                                        size="s"
                                        text={t('studio.upload.clearAll')}
                                        onClick={clearAllFiles}
                                        disabled={isFileListPrepareUi}
                                        showStartIcon
                                        startIcon={<DeleteOutlineIcon sx={{ fontSize: 18 }} />}
                                    />
                                </Box>
                                {isFileListPreviewTruncated && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'block', mb: 1 }}
                                    >
                                        {t('studio.upload.fileListPreviewTruncated', {
                                            shown: FILE_LIST_PREVIEW_LIMIT,
                                            total: uploadItems.length,
                                        })}
                                    </Typography>
                                )}
                                <List dense sx={{ maxHeight: 300, overflow: 'auto', bgcolor: 'background.paper' }}>
                                    {visibleUploadItems.map((item, index) => (
                                        <ListItem
                                            key={`${item.fileName}-${item.totalSize}-${index}`}
                                            divider
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    onClick={() => removeFile(item.clientId)}
                                                    sx={{ color: '#bdbdbd', '&:hover': { color: '#f44336' } }}
                                                >
                                                    <CancelIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemText
                                                primary={item.rawFileName || item.fileName}
                                                secondary={`${Math.round(item.totalSize / 1024)} KB`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                                {uploadItems.length > FILE_LIST_PREVIEW_LIMIT && (
                                    <Box sx={{ mt: 1.5, display: 'flex', justifyContent: 'center' }}>
                                        <SCUIButton
                                            type="secondary"
                                            sizeType="default"
                                            size="s"
                                            text={
                                                fileListPreviewExpanded
                                                    ? t('studio.upload.showLessFiles')
                                                    : t('studio.upload.showAllFiles', { count: uploadItems.length })
                                            }
                                            onClick={() => setFileListPreviewExpanded((v) => !v)}
                                        />
                                    </Box>
                                )}
                            </Box>
                        )}

                        {isFileListPrepareUi && (
                            <Box sx={{ mt: 2, mb: 0.5 }}>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ display: 'block', mb: 0.75 }}
                                >
                                    {fileListIngest?.mode === 'ingesting'
                                        ? t('studio.upload.fileListPreparingProgress', {
                                              current: fileListIngest.current,
                                              total: fileListIngest.total,
                                          })
                                        : t('studio.upload.fileListPrepareBottomHint')}
                                </Typography>
                                <LinearProgress
                                    variant={fileListIngest?.mode === 'ingesting' ? 'determinate' : 'indeterminate'}
                                    value={
                                        fileListIngest?.mode === 'ingesting' && fileListIngest.total > 0
                                            ? Math.min(100, (fileListIngest.current / fileListIngest.total) * 100)
                                            : undefined
                                    }
                                    sx={{ height: 6, borderRadius: 1 }}
                                />
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <SCUIButton
                                type="secondary"
                                sizeType="default"
                                size="s"
                                text={t('general.back')}
                                onClick={handleBack}
                                disabled={isFileListPrepareUi}
                                showStartIcon
                                startIcon={<ArrowBackIcon />}
                            />
                            <SCUIButton
                                type="primary"
                                sizeType="default"
                                size="s"
                                text={t('general.next')}
                                onClick={handleNext}
                                disabled={!isStep2Valid}
                                showStartIcon
                                startIcon={<ArrowForwardIcon sx={{ color: '#fff' }} />}
                            />
                        </Box>
                    </Box>
                )}

                {/* Step 3: S3 Destination Selection */}
                {activeStep === 2 && (
                    <Box
                        sx={{
                            p: 3,
                            mb: 4,
                            borderRadius: 3,
                            border: '1px solid #e0e0e0',
                            backgroundColor: '#fff',
                        }}
                    >
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="h6">{t('aiPrompt.s3Browser.title')}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('studio.upload.selectDestination')}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                height: 400,
                                border: '1px solid #ddd',
                                borderRadius: 2,
                                overflow: 'hidden',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <SCUIS3Browser
                                basePrefix={studioIglooShareS3Prefix}
                                readOnly={false}
                                disableUpload={true}
                                disableCreateFolder={false}
                                disableDelete={true}
                                disableRename={true}
                                disableMove={true}
                                disableDownload={true}
                                showPreviewPanel={false}
                                disableFileSelection={true}
                                onPrefixChange={(prefix) => setS3DestinationPath(prefix)}
                                hideContentWrapper={true}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <SCUIButton
                                type="secondary"
                                sizeType="default"
                                size="s"
                                text={t('general.back')}
                                onClick={handleBack}
                                showStartIcon
                                startIcon={<ArrowBackIcon />}
                            />
                            <SCUIButton
                                type="primary"
                                sizeType="default"
                                size="s"
                                text={t('general.next')}
                                onClick={handleNext}
                                disabled={!isS3DestinationUnderIglooShare}
                                showStartIcon
                                startIcon={<ArrowForwardIcon sx={{ color: '#fff' }} />}
                            />
                        </Box>
                    </Box>
                )}

                {/* Step 4: Confirmation and Progress */}
                {activeStep === 3 && (
                    <Box sx={{ width: '100%' }}>
                        {status && (
                            <Box
                                sx={{
                                    mb: 2,
                                    p: 2,
                                    backgroundColor: '#e3f2fd',
                                    border: '1px solid #bbdefb',
                                    borderRadius: 2,
                                    color: '#1565c0',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                }}
                            >
                                <Typography component="span" variant="body2" sx={{ fontWeight: 500 }}>
                                    {status}
                                </Typography>
                            </Box>
                        )}

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', lg: 'row' },
                                gap: 3,
                                alignItems: 'flex-start',
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%', lg: '58%' } }}>
                                <Card sx={{ mb: { xs: 0, lg: 0 } }}>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {t('general.summary')}
                                        </Typography>
                                        <Divider sx={{ mb: 2 }} />

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Typography variant="body1">
                                                <strong>{t('emailTemplateBuilder.countryCode')}:</strong>{' '}
                                                {getSelectionSummary().country}
                                            </Typography>
                                            <Typography variant="body1">
                                                <strong>{t('studio.grpcFileManager.shootingType.label')}:</strong>{' '}
                                                {getSelectionSummary().shootingType}
                                            </Typography>
                                            {isMannequinMandatory && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography variant="body1">
                                                        <strong>{t('studio.grpcFileManager.mannequin')}:</strong>{' '}
                                                        {getSelectionSummary().mannequin}
                                                    </Typography>
                                                    {getSelectionSummary().mannequinIsAi ? (
                                                        <Chip
                                                            icon={<SmartToyIcon sx={{ fontSize: 16 }} />}
                                                            label="AI"
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: '#ede7f6',
                                                                color: '#7c4dff',
                                                                fontWeight: 600,
                                                                height: 24,
                                                            }}
                                                        />
                                                    ) : (
                                                        <Chip
                                                            icon={<PersonIcon sx={{ fontSize: 16 }} />}
                                                            label={t('mannequin.real')}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: '#e3f2fd',
                                                                color: '#1976d2',
                                                                fontWeight: 600,
                                                                height: 24,
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                            <Typography variant="body1">
                                                <strong>{t('studio.grpcFileManager.role')}:</strong>{' '}
                                                {getSelectionSummary().authoritiesCount} {t('general.person')}
                                            </Typography>
                                            <Typography variant="body1">
                                                <strong>{t('studio.upload.selectedFiles')}:</strong>{' '}
                                                {getSelectionSummary().filesCount} {t('general.file')}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                sx={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                                            >
                                                <strong>
                                                    {t('studio.grpcFileManager.summary.uploadDestinationFolder')}:
                                                </strong>{' '}
                                                {(() => {
                                                    const path = s3DestinationPath?.trim();
                                                    if (!path) return '—';
                                                    const envPrefix = trimTrailingSlashes(
                                                        String(import.meta.env.VITE_ENVIRONMENT ?? '').trim()
                                                    );
                                                    const baseToHide = envPrefix
                                                        ? `${envPrefix}/igloo_share/`
                                                        : 'igloo_share/';
                                                    if (path.toLowerCase().startsWith(baseToHide.toLowerCase())) {
                                                        return path.substring(baseToHide.length);
                                                    }
                                                    return path;
                                                })()}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>

                                {uploadItems.some((i) => i.status === UploadStatus.Failed && !!i.errorMessage) && (
                                    <Card
                                        sx={{
                                            mt: 2,
                                            border: '1px solid #ffcdd2',
                                            backgroundColor: '#fff8f8',
                                        }}
                                    >
                                        <CardContent>
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: 600, mb: 1, color: '#c62828' }}
                                            >
                                                {t('studio.upload.createValidationErrorsTitle')}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                {t('studio.upload.createValidationErrorsHint')}
                                            </Typography>
                                            <List dense sx={{ maxHeight: 280, overflow: 'auto', py: 0 }}>
                                                {uploadItems
                                                    .filter(
                                                        (i) =>
                                                            i.status === UploadStatus.Failed &&
                                                            !!i.errorMessage &&
                                                            !i.storedFilename
                                                    )
                                                    .map((item, idx) => (
                                                        <ListItem
                                                            key={`create-fail-${item.clientId}-${idx}`}
                                                            alignItems="flex-start"
                                                            sx={{
                                                                px: 0,
                                                                borderBottom: '1px solid #ffebee',
                                                                '&:last-child': { borderBottom: 'none' },
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={
                                                                    <Typography
                                                                        variant="body2"
                                                                        sx={{ fontWeight: 600, wordBreak: 'break-all' }}
                                                                    >
                                                                        {item.rawFileName || item.fileName}
                                                                    </Typography>
                                                                }
                                                                secondary={
                                                                    <Typography
                                                                        variant="caption"
                                                                        sx={{
                                                                            color: '#c62828',
                                                                            display: 'block',
                                                                            mt: 0.5,
                                                                        }}
                                                                    >
                                                                        {item.errorMessage}
                                                                    </Typography>
                                                                }
                                                            />
                                                        </ListItem>
                                                    ))}
                                            </List>
                                        </CardContent>
                                    </Card>
                                )}
                            </Box>

                            {uploadItems.length > 0 && (
                                <Box
                                    sx={{
                                        width: { xs: '100%', lg: '42%' },
                                        minWidth: 0,
                                        alignSelf: 'stretch',
                                    }}
                                >
                                    <Accordion
                                        expanded={uploadStatusAccordionExpanded}
                                        onChange={(_, expanded) => setUploadStatusAccordionExpanded(expanded)}
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                            border: '1px solid',
                                            borderColor: isUploading ? 'primary.light' : 'divider',
                                            borderRadius: 2,
                                            overflow: 'hidden',
                                            backgroundColor: 'background.paper',
                                            height: '100%',
                                            boxShadow: isUploading
                                                ? '0 2px 12px rgba(25, 118, 210, 0.12)'
                                                : '0 1px 4px rgba(0,0,0,0.06)',
                                            '&:before': { display: 'none' },
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={
                                                <ExpandMoreIcon
                                                    sx={{
                                                        color: isUploading ? 'primary.main' : 'action.active',
                                                    }}
                                                />
                                            }
                                            sx={{
                                                minHeight: 56,
                                                px: 2,
                                                py: 0.5,
                                                background: isUploading
                                                    ? 'linear-gradient(105deg, #e3f2fd 0%, #f3e5f5 100%)'
                                                    : 'linear-gradient(180deg, #fafafa 0%, #ffffff 100%)',
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                                '& .MuiAccordionSummary-content': {
                                                    my: 1,
                                                    alignItems: 'center',
                                                    gap: 1.25,
                                                    flexWrap: 'wrap',
                                                },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    minWidth: 0,
                                                    mr: 0.5,
                                                }}
                                            >
                                                <CloudUploadIcon
                                                    sx={{
                                                        fontSize: 26,
                                                        color: isUploading ? 'primary.main' : 'text.secondary',
                                                        filter: isUploading
                                                            ? 'drop-shadow(0 1px 2px rgba(25,118,210,0.35))'
                                                            : 'none',
                                                    }}
                                                />
                                                <Typography
                                                    variant="subtitle1"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: isUploading ? 'primary.dark' : 'text.primary',
                                                        letterSpacing: 0.02,
                                                    }}
                                                >
                                                    {t('studio.upload.uploadStatus')}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                icon={
                                                    <ListIcon
                                                        sx={{
                                                            fontSize: 18,
                                                            color: isUploading
                                                                ? 'primary.contrastText'
                                                                : 'primary.main',
                                                        }}
                                                    />
                                                }
                                                label={`${completedFilesCount}/${uploadItems.length}`}
                                                size="small"
                                                sx={(theme) => ({
                                                    fontWeight: 700,
                                                    fontVariantNumeric: 'tabular-nums',
                                                    bgcolor: isUploading
                                                        ? theme.palette.primary.main
                                                        : alpha(theme.palette.primary.main, 0.12),
                                                    color: isUploading
                                                        ? theme.palette.primary.contrastText
                                                        : theme.palette.primary.dark,
                                                    border: '1px solid',
                                                    borderColor: isUploading
                                                        ? theme.palette.primary.dark
                                                        : theme.palette.primary.light,
                                                    '& .MuiChip-icon': { ml: '4px' },
                                                })}
                                            />
                                            {(isUploading || lastTransferDurationMs !== null) && (
                                                <Tooltip
                                                    title={
                                                        isUploading
                                                            ? t('studio.upload.transferElapsed', {
                                                                  time: formatTransferDuration(transferElapsedMs),
                                                              })
                                                            : t('studio.upload.transferTotalDuration', {
                                                                  time: formatTransferDuration(lastTransferDurationMs!),
                                                              })
                                                    }
                                                    arrow
                                                    placement="top"
                                                >
                                                    <Chip
                                                        icon={
                                                            isUploading ? (
                                                                <HourglassEmptyOutlinedIcon
                                                                    sx={{ fontSize: 18, color: 'warning.main' }}
                                                                />
                                                            ) : (
                                                                <AccessTimeIcon
                                                                    sx={{ fontSize: 18, color: 'success.main' }}
                                                                />
                                                            )
                                                        }
                                                        label={formatTransferDuration(
                                                            isUploading ? transferElapsedMs : lastTransferDurationMs!
                                                        )}
                                                        size="small"
                                                        sx={(theme) => ({
                                                            fontWeight: 700,
                                                            fontVariantNumeric: 'tabular-nums',
                                                            bgcolor: isUploading
                                                                ? alpha(theme.palette.warning.main, 0.14)
                                                                : alpha(theme.palette.success.main, 0.14),
                                                            color: isUploading
                                                                ? theme.palette.warning.dark
                                                                : theme.palette.success.dark,
                                                            border: '1px solid',
                                                            borderColor: isUploading
                                                                ? theme.palette.warning.light
                                                                : theme.palette.success.light,
                                                            '& .MuiChip-icon': { ml: '4px' },
                                                        })}
                                                    />
                                                </Tooltip>
                                            )}
                                        </AccordionSummary>
                                        <AccordionDetails
                                            sx={{
                                                px: 2,
                                                pb: 2,
                                                pt: 0,
                                                maxHeight: { xs: 320, sm: 480 },
                                                overflowY: 'auto',
                                            }}
                                        >
                                            <Box sx={{ mb: 2 }}>
                                                <Chip
                                                    icon={
                                                        <ReplayIcon
                                                            sx={{
                                                                color: autoRetryFailed
                                                                    ? 'warning.dark'
                                                                    : 'text.secondary',
                                                            }}
                                                        />
                                                    }
                                                    label={
                                                        <Box
                                                            sx={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'space-between',
                                                                gap: 1.5,
                                                                width: '100%',
                                                            }}
                                                        >
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 700,
                                                                    color: autoRetryFailed
                                                                        ? 'warning.dark'
                                                                        : 'text.primary',
                                                                }}
                                                            >
                                                                {t('studio.upload.autoRetryFailed')}
                                                            </Typography>
                                                            <Switch
                                                                size="small"
                                                                checked={autoRetryFailed}
                                                                onChange={(event) =>
                                                                    setAutoRetryFailed(event.target.checked)
                                                                }
                                                                onClick={(event) => event.stopPropagation()}
                                                                color="warning"
                                                                sx={{
                                                                    '& .MuiSwitch-switchBase.Mui-checked': {
                                                                        color: 'warning.main',
                                                                    },
                                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                                                                        {
                                                                            backgroundColor: 'warning.main',
                                                                        },
                                                                }}
                                                            />
                                                        </Box>
                                                    }
                                                    sx={(theme) => ({
                                                        width: '100%',
                                                        height: 'auto',
                                                        justifyContent: 'flex-start',
                                                        py: 1,
                                                        px: 0.5,
                                                        borderRadius: 2,
                                                        border: '1px solid',
                                                        borderColor: autoRetryFailed
                                                            ? theme.palette.warning.light
                                                            : theme.palette.divider,
                                                        bgcolor: autoRetryFailed
                                                            ? alpha(theme.palette.warning.main, 0.16)
                                                            : alpha(theme.palette.grey[500], 0.08),
                                                        boxShadow: autoRetryFailed
                                                            ? `0 2px 10px ${alpha(theme.palette.warning.main, 0.22)}`
                                                            : 'none',
                                                        transition:
                                                            'background-color 0.2s, border-color 0.2s, box-shadow 0.2s',
                                                        '& .MuiChip-icon': {
                                                            ml: 1,
                                                            mr: 0,
                                                        },
                                                        '& .MuiChip-label': {
                                                            display: 'block',
                                                            width: '100%',
                                                            px: 1,
                                                            py: 0.25,
                                                        },
                                                    })}
                                                />
                                                {isAutoRetryInProgress && (
                                                    <Box
                                                        sx={(theme) => ({
                                                            mt: 1,
                                                            px: 1.25,
                                                            py: 0.75,
                                                            borderRadius: 1.5,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.75,
                                                            bgcolor: alpha(theme.palette.info.main, 0.1),
                                                            border: '1px solid',
                                                            borderColor: alpha(theme.palette.info.main, 0.35),
                                                        })}
                                                    >
                                                        <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main' }} />
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: 'info.dark',
                                                                fontWeight: 600,
                                                            }}
                                                        >
                                                            {t('studio.upload.autoRetryInProgress')}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                            {/* Devam Ediyor / Bekleyenler */}
                                            <Accordion
                                                expanded={ongoingSubExpanded}
                                                onChange={(_, expanded) => setOngoingSubExpanded(expanded)}
                                                elevation={0}
                                                disableGutters
                                                sx={{
                                                    border: '1px solid #ffe0b2',
                                                    borderRadius: '8px !important',
                                                    overflow: 'hidden',
                                                    mb: 2,
                                                    '&:before': { display: 'none' },
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon sx={{ color: 'warning.dark' }} />}
                                                    sx={{
                                                        background: 'linear-gradient(180deg, #fff3e0 0%, #ffe0b2 100%)',
                                                        minHeight: 48,
                                                        height: 48,
                                                        px: 2,
                                                        '& .MuiAccordionSummary-content': {
                                                            my: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            width: '100%',
                                                            pr: 1,
                                                        },
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                        <HourglassEmptyOutlinedIcon
                                                            sx={{ color: 'warning.dark', fontSize: 20 }}
                                                        />
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{ fontWeight: 700, color: 'warning.dark' }}
                                                        >
                                                            {t('studio.upload.statusOngoing')}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={`${ongoingGroup.length}/${uploadItems.length}`}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 700,
                                                            bgcolor: 'warning.main',
                                                            color: 'warning.contrastText',
                                                            height: 20,
                                                            fontSize: '11px',
                                                        }}
                                                    />
                                                </AccordionSummary>
                                                <AccordionDetails
                                                    sx={{
                                                        p: 1.5,
                                                        maxHeight: 300,
                                                        overflowY: 'auto',
                                                        bgcolor: '#fcfcfc',
                                                    }}
                                                >
                                                    {ongoingGroup.length === 0 ? (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontStyle: 'italic',
                                                                textAlign: 'center',
                                                                py: 2,
                                                            }}
                                                        >
                                                            {t('general.noDataFound', 'Kayıt bulunamadı')}
                                                        </Typography>
                                                    ) : (
                                                        ongoingGroup.map((item) => renderUploadItemCard(item))
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>

                                            {/* Başarılı Olanlar */}
                                            <Accordion
                                                expanded={successSubExpanded}
                                                onChange={(_, expanded) => setSuccessSubExpanded(expanded)}
                                                elevation={0}
                                                disableGutters
                                                sx={{
                                                    border: '1px solid #c8e6c9',
                                                    borderRadius: '8px !important',
                                                    overflow: 'hidden',
                                                    mb: 2,
                                                    '&:before': { display: 'none' },
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon sx={{ color: 'success.dark' }} />}
                                                    sx={{
                                                        background: 'linear-gradient(180deg, #e8f5e9 0%, #c8e6c9 100%)',
                                                        minHeight: 48,
                                                        height: 48,
                                                        px: 2,
                                                        '& .MuiAccordionSummary-content': {
                                                            my: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            width: '100%',
                                                            pr: 1,
                                                        },
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                        <CheckCircleIcon sx={{ color: 'success.dark', fontSize: 20 }} />
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{ fontWeight: 700, color: 'success.dark' }}
                                                        >
                                                            {t('studio.upload.statusSuccess')}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={`${successGroup.length}/${uploadItems.length}`}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 700,
                                                            bgcolor: 'success.main',
                                                            color: 'success.contrastText',
                                                            height: 20,
                                                            fontSize: '11px',
                                                        }}
                                                    />
                                                </AccordionSummary>
                                                <AccordionDetails
                                                    sx={{
                                                        p: 1.5,
                                                        maxHeight: 300,
                                                        overflowY: 'auto',
                                                        bgcolor: '#fcfcfc',
                                                    }}
                                                >
                                                    {successGroup.length === 0 ? (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontStyle: 'italic',
                                                                textAlign: 'center',
                                                                py: 2,
                                                            }}
                                                        >
                                                            {t('general.noDataFound', 'Kayıt bulunamadı')}
                                                        </Typography>
                                                    ) : (
                                                        successGroup.map((item) => renderUploadItemCard(item))
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>

                                            {/* Başarısız/Hatalı Olanlar */}
                                            <Accordion
                                                expanded={failedSubExpanded}
                                                onChange={(_, expanded) => setFailedSubExpanded(expanded)}
                                                elevation={0}
                                                disableGutters
                                                sx={{
                                                    border: '1px solid #ffcdd2',
                                                    borderRadius: '8px !important',
                                                    overflow: 'hidden',
                                                    '&:before': { display: 'none' },
                                                }}
                                            >
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon sx={{ color: 'error.dark' }} />}
                                                    sx={{
                                                        background: 'linear-gradient(180deg, #ffebee 0%, #ffcdd2 100%)',
                                                        minHeight: 48,
                                                        height: 48,
                                                        px: 2,
                                                        '& .MuiAccordionSummary-content': {
                                                            my: 1,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            width: '100%',
                                                            pr: 1,
                                                        },
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                                        <CancelIcon sx={{ color: 'error.dark', fontSize: 20 }} />
                                                        <Typography
                                                            variant="subtitle2"
                                                            sx={{ fontWeight: 700, color: 'error.dark' }}
                                                        >
                                                            {t('studio.upload.statusFailed')}
                                                        </Typography>
                                                    </Box>
                                                    <Chip
                                                        label={`${failedGroup.length}/${uploadItems.length}`}
                                                        size="small"
                                                        sx={{
                                                            fontWeight: 700,
                                                            bgcolor: 'error.main',
                                                            color: 'error.contrastText',
                                                            height: 20,
                                                            fontSize: '11px',
                                                        }}
                                                    />
                                                </AccordionSummary>
                                                <AccordionDetails
                                                    sx={{
                                                        p: 1.5,
                                                        maxHeight: 300,
                                                        overflowY: 'auto',
                                                        bgcolor: '#fcfcfc',
                                                    }}
                                                >
                                                    {failedGroup.length === 0 ? (
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: 'text.secondary',
                                                                fontStyle: 'italic',
                                                                textAlign: 'center',
                                                                py: 2,
                                                            }}
                                                        >
                                                            {t('general.noDataFound', 'Kayıt bulunamadı')}
                                                        </Typography>
                                                    ) : (
                                                        failedGroup.map((item) => renderUploadItemCard(item))
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        </AccordionDetails>
                                    </Accordion>
                                </Box>
                            )}
                        </Box>

                        {!isUploading && uploadItems.some((i) => i.status !== UploadStatus.Pending) && (
                            <Box
                                sx={{
                                    mt: 3,
                                    p: 2.5,
                                    borderRadius: 2,
                                    border: '1px solid #e0e0e0',
                                    backgroundColor: '#fafafa',
                                }}
                            >
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                                    {t('studio.upload.uploadResult')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                backgroundColor: '#e8f5e9',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CheckCircleIcon sx={{ color: '#4CAF50', fontSize: 24 }} />
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="h5"
                                                sx={{ fontWeight: 700, color: '#4CAF50', lineHeight: 1 }}
                                            >
                                                {uploadItems.filter((i) => i.status === UploadStatus.Completed).length}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                {t('general.success')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                backgroundColor: '#ffebee',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <CancelIcon sx={{ color: '#F44336', fontSize: 24 }} />
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="h5"
                                                sx={{ fontWeight: 700, color: '#F44336', lineHeight: 1 }}
                                            >
                                                {uploadItems.filter((i) => i.status === UploadStatus.Failed).length}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                {t('studio.upload.failed')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                backgroundColor: '#e3f2fd',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            <ErrorIcon sx={{ color: '#2196F3', fontSize: 24 }} />
                                        </Box>
                                        <Box>
                                            <Typography
                                                variant="h5"
                                                sx={{ fontWeight: 700, color: '#2196F3', lineHeight: 1 }}
                                            >
                                                {uploadItems.length}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#666' }}>
                                                {t('studio.upload.total')}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                                {(() => {
                                    const successfulCount = uploadItems.filter(
                                        (i) => i.status === UploadStatus.Completed
                                    ).length;
                                    const failedCount = uploadItems.filter(
                                        (i) => i.status === UploadStatus.Failed
                                    ).length;

                                    if (successfulCount > 0 && failedCount > 0) {
                                        return (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    backgroundColor: '#fff8e1',
                                                    border: '1px solid #ffcc80',
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        color: '#e65100',
                                                        fontWeight: 600,
                                                        textAlign: 'center',
                                                    }}
                                                >
                                                    {t('studio.upload.partialUploadsCompletedMessage', {
                                                        success: successfulCount,
                                                        failed: failedCount,
                                                    })}
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                    if (successfulCount > 0 && failedCount === 0) {
                                        return (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    backgroundColor: '#e8f5e9',
                                                    border: '1px solid #a5d6a7',
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: '#2e7d32', fontWeight: 600, textAlign: 'center' }}
                                                >
                                                    {t('studio.upload.allUploadsCompleted')}
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                    if (failedCount > 0 && successfulCount === 0) {
                                        return (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    p: 1.5,
                                                    borderRadius: 1,
                                                    backgroundColor: '#ffebee',
                                                    border: '1px solid #ffcdd2',
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: '#c62828', fontWeight: 600, textAlign: 'center' }}
                                                >
                                                    {t('studio.upload.allUploadsFailed')}
                                                </Typography>
                                            </Box>
                                        );
                                    }
                                    return null;
                                })()}
                                {uploadItems.some((i) => i.status === UploadStatus.Failed && !!i.errorMessage) && (
                                    <Box sx={{ mt: 2 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ fontWeight: 600, mb: 1, color: '#c62828' }}
                                        >
                                            {t('studio.upload.failedFilesDetail')}
                                        </Typography>
                                        <List dense disablePadding>
                                            {uploadItems
                                                .filter((i) => i.status === UploadStatus.Failed && !!i.errorMessage)
                                                .map((i) => (
                                                    <ListItem
                                                        key={i.clientId}
                                                        alignItems="flex-start"
                                                        sx={{
                                                            px: 1.5,
                                                            py: 1,
                                                            mb: 1,
                                                            borderRadius: 1,
                                                            backgroundColor: '#fff',
                                                            border: '1px solid #ffcdd2',
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={i.rawFileName || i.fileName}
                                                            secondary={i.errorMessage}
                                                            slotProps={{
                                                                primary: {
                                                                    variant: 'body2',
                                                                    fontWeight: 600,
                                                                },
                                                                secondary: {
                                                                    variant: 'caption',
                                                                    color: 'error',
                                                                    sx: { whiteSpace: 'pre-wrap', mt: 0.5 },
                                                                },
                                                            }}
                                                        />
                                                    </ListItem>
                                                ))}
                                        </List>
                                    </Box>
                                )}
                            </Box>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                            <SCUIButton
                                type="secondary"
                                sizeType="default"
                                size="s"
                                text={t('general.back')}
                                onClick={handleBack}
                                disabled={isUploading}
                                showStartIcon
                                startIcon={<ArrowBackIcon />}
                            />
                            {showCompleteButton ? (
                                <SCUIButton
                                    type="primary"
                                    sizeType="default"
                                    size="s"
                                    text={isUploading ? t('studio.upload.uploading') : t('general.complete')}
                                    onClick={handleComplete}
                                    disabled={isUploading || isSelectionLoading}
                                />
                            ) : (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    {uploadItems.some((i) => i.status === UploadStatus.Failed) && (
                                        <SCUIButton
                                            type="primary"
                                            sizeType="default"
                                            size="s"
                                            text={t('studio.upload.reUpload')}
                                            onClick={handleComplete}
                                            disabled={isUploading || isSelectionLoading}
                                            showStartIcon
                                            startIcon={<ReplayIcon sx={{ color: '#fff' }} />}
                                        />
                                    )}
                                    <SCUIButton
                                        type={
                                            uploadItems.some((i) => i.status === UploadStatus.Failed)
                                                ? 'secondary'
                                                : 'primary'
                                        }
                                        sizeType="default"
                                        size="s"
                                        text={t('general.newProcess')}
                                        onClick={resetForm}
                                        disabled={isUploading}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>
        </SCUIContent>
    );
};
